import { StyleSheet } from "react-native-unistyles";
import { Autocomplete } from "@visioni/a3s-ui/components";
import { View } from "@src/components";
import type { RawActivationFilters } from "@visioni/a3s-monitoring/app";
import type { CameraOption, SiteOption } from "../hooks/useActivationScopeOptions";
import { ActivationDateRangeField } from "./ActivationDateRangeField";

/** The filters as the URL carries them: ids, and calendar days as `YYYY-MM-DD`. */
export type ActivationFilterValues = {
  readonly [K in keyof RawActivationFilters]?: string;
};

interface ActivationFilterBarProps {
  readonly values: ActivationFilterValues;
  readonly onChange: (next: ActivationFilterValues) => void;
  readonly sites: readonly SiteOption[];
  readonly cameras: readonly CameraOption[];
  readonly isLoading: boolean;
}

const siteId = (s: SiteOption) => s.id;
const siteLabel = (s: SiteOption) => s.label;
const cameraKey = (c: CameraOption) => c.key;
const cameraLabel = (c: CameraOption) => c.label;

/**
 * Site, then optionally a camera at that site, and a range of days — laid out
 * like the clip compilation filters so the two support tools read alike.
 *
 * The camera picker offers only the selected site's cameras, and changing the
 * site clears the camera: the replica indexes a camera under its site, so a
 * camera from another site would filter to nothing.
 *
 * A filter the URL applies for a site or camera this tenant no longer lists
 * shows its raw id, so a shared or detail-page link never looks unfiltered.
 */
export function ActivationFilterBar({ values, onChange, sites, cameras, isLoading }: ActivationFilterBarProps) {
  const selectedSite: SiteOption | null =
    values.site === undefined ?
      null
    : (sites.find((s) => s.id === values.site) ?? { id: values.site, label: values.site });
  const siteCameras = values.site === undefined ? [] : cameras.filter((c) => c.siteId === values.site);
  const selectedCamera: CameraOption | null =
    values.camera === undefined ?
      null
    : (siteCameras.find((c) => c.key === values.camera) ?? {
        key: values.camera,
        siteId: values.site ?? "",
        label: values.camera,
      });

  const update = (patch: ActivationFilterValues) => onChange({ ...values, ...patch });

  return (
    <View style={styles.row}>
      <View style={[styles.field, styles.top]}>
        <Autocomplete<SiteOption>
          options={sites}
          value={selectedSite}
          onChange={(site) => update({ site: site?.id, camera: undefined })}
          getOptionLabel={siteLabel}
          getOptionId={siteId}
          isLoading={isLoading}
          placeholder="All sites"
          label="Site"
        />
      </View>
      <View style={[styles.field, styles.middle]}>
        <Autocomplete<CameraOption>
          options={siteCameras}
          value={selectedCamera}
          onChange={(camera) => update({ camera: camera?.key })}
          getOptionLabel={cameraLabel}
          getOptionId={cameraKey}
          isLoading={isLoading}
          placeholder={values.site === undefined ? "Choose a site first" : "All cameras"}
          label="Camera"
        />
      </View>
      <View style={[styles.field, styles.bottom]}>
        <ActivationDateRangeField value={values} onChange={update} placeholder="All dates" />
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  // Same proportions as the clip compilation filters.
  row: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: theme.spacing.lg },
  field: { flexBasis: "22%", flexGrow: 1, minWidth: 160 },
  // Each dropdown opens over the fields after it, so earlier fields stack higher.
  top: { zIndex: 3 },
  middle: { zIndex: 2 },
  bottom: { zIndex: 1 },
}));
